import { ACCOUNT_TYPE_LABELS } from '@bcube/shared/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { sectorApi } from '@/api/commercial/sectors';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LocalAccount } from '@/context/create-account-flow';
import { maskRg } from '@/lib/formatters';

const TYPE_IE_OPTIONS = ['Normal', 'Contribuinte', 'Não Contribuinte', 'Isento'];

const SITUATION_IE_OPTIONS = [
  { id: '1', text: 'Habilitada', status: true },
  { id: '2', text: 'Não habilitada', status: false },
  { id: '3', text: 'Suspensa', status: false },
  { id: '4', text: 'Cancelada', status: false },
  { id: '5', text: 'Baixada', status: false },
];

// Schema for CNPJ accounts
const cnpjAccountSchema = z.object({
  social_name: z.string().min(1, 'Razão social é obrigatória'),
  fantasy_name: z.string().optional(),
  state_registration: z.string().optional(),
  municipal_registration: z.string().optional(),
  status: z.string().optional(),
  situationIE: z
    .object({
      id: z.string(),
      text: z.string(),
      status: z.boolean(),
    })
    .optional(),
  typeIE: z.string().optional(),
  setor: z.array(z.string()).optional(),
});

// Schema for CPF accounts
const cpfAccountSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  rg: z.string().optional(),
});

type CnpjFormData = z.infer<typeof cnpjAccountSchema>;
type CpfFormData = z.infer<typeof cpfAccountSchema>;

interface AccountDataUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<LocalAccount>) => void;
  account: LocalAccount;
}

// CNPJA-sourced fields: if they have a value, CNPJA populated them → read-only
const CNPJA_FIELDS = [
  'social_name',
  'fantasy_name',
  'state_registration',
  'status',
  'situationIE',
  'typeIE',
] as const;

function isCnpjaFieldDisabled(account: LocalAccount, field: (typeof CNPJA_FIELDS)[number]) {
  const value = account[field];
  if (value === undefined || value === null || value === '') return false;
  // For situationIE (object), check if it has an id
  if (typeof value === 'object' && 'id' in value) return !!value.id;
  return true;
}

export function AccountDataUpdateDialog({
  open,
  onClose,
  onSubmit,
  account,
}: AccountDataUpdateDialogProps) {
  const isCnpj = account.document.type === 'cnpj';

  const { data: sectorsData } = useQuery({
    queryKey: ['sectors', { enabled: true }],
    queryFn: () => sectorApi.findMany({ enabled: true, limit: 100 }),
    enabled: open && isCnpj,
  });
  const sectorOptions = sectorsData?.docs.map((s) => s.name) || [];

  // CNPJ form
  const cnpjForm = useForm<CnpjFormData>({
    resolver: zodResolver(cnpjAccountSchema),
    defaultValues: {
      social_name: '',
      fantasy_name: '',
      state_registration: '',
      municipal_registration: '',
      status: '',
      situationIE: undefined,
      typeIE: '',
      setor: [],
    },
  });

  // CPF form
  const cpfForm = useForm<CpfFormData>({
    resolver: zodResolver(cpfAccountSchema),
    defaultValues: {
      name: '',
      rg: '',
    },
  });

  const selectedSetor = cnpjForm.watch('setor') || [];

  // Reset forms when dialog opens
  useEffect(() => {
    if (open) {
      if (isCnpj) {
        cnpjForm.reset({
          social_name: account.social_name || '',
          fantasy_name: account.fantasy_name || '',
          state_registration: account.state_registration || '',
          municipal_registration: account.municipal_registration || '',
          status: account.status || '',
          situationIE: account.situationIE || undefined,
          typeIE: account.typeIE || '',
          setor: account.setor || [],
        });
      } else {
        cpfForm.reset({
          name: account.name || '',
          rg: maskRg(account.rg || ''),
        });
      }
    }
  }, [open, account, isCnpj, cnpjForm, cpfForm]);

  const handleSetorChange = (option: string, checked: boolean) => {
    const current = selectedSetor;
    if (checked) {
      cnpjForm.setValue('setor', [...current, option]);
    } else {
      cnpjForm.setValue(
        'setor',
        current.filter((s) => s !== option),
      );
    }
  };

  const handleCnpjSubmit = (data: CnpjFormData) => {
    onSubmit({
      social_name: data.social_name,
      fantasy_name: data.fantasy_name,
      state_registration: data.state_registration,
      municipal_registration: data.municipal_registration,
      status: data.status,
      situationIE: data.situationIE,
      typeIE: data.typeIE,
      setor: data.setor,
    });
    onClose();
  };

  const handleCpfSubmit = (data: CpfFormData) => {
    onSubmit({
      name: data.name,
      rg: data.rg,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Dados da Conta</DialogTitle>
        </DialogHeader>

        {isCnpj ? (
          <form onSubmit={cnpjForm.handleSubmit(handleCnpjSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="social_name">Razão Social *</Label>
              <Input
                id="social_name"
                {...cnpjForm.register('social_name')}
                disabled={isCnpjaFieldDisabled(account, 'social_name')}
                placeholder="Razão social da empresa"
              />
              {cnpjForm.formState.errors.social_name && (
                <p className="text-sm text-red-500">
                  {cnpjForm.formState.errors.social_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="fantasy_name">Nome Fantasia</Label>
              <Input
                id="fantasy_name"
                {...cnpjForm.register('fantasy_name')}
                disabled={isCnpjaFieldDisabled(account, 'fantasy_name')}
                placeholder="Nome fantasia"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state_registration">Inscrição Estadual</Label>
                <Input
                  id="state_registration"
                  {...cnpjForm.register('state_registration')}
                  disabled={isCnpjaFieldDisabled(account, 'state_registration')}
                  placeholder="IE"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="municipal_registration">Inscrição Municipal</Label>
                <Input
                  id="municipal_registration"
                  {...cnpjForm.register('municipal_registration')}
                  placeholder="IM"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Situação IE</Label>
                <Controller
                  control={cnpjForm.control}
                  name="situationIE"
                  render={({ field }) => (
                    <Select
                      value={field.value?.id || ''}
                      onValueChange={(value) => {
                        const option = SITUATION_IE_OPTIONS.find((o) => o.id === value);
                        field.onChange(option);
                      }}
                      disabled={isCnpjaFieldDisabled(account, 'situationIE')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {SITUATION_IE_OPTIONS.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo IE</Label>
                <Controller
                  control={cnpjForm.control}
                  name="typeIE"
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      disabled={isCnpjaFieldDisabled(account, 'typeIE')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_IE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Situação CNPJ</Label>
              <Controller
                control={cnpjForm.control}
                name="status"
                render={({ field }) => (
                  <Select
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    disabled={isCnpjaFieldDisabled(account, 'status')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ativa">Ativa</SelectItem>
                      <SelectItem value="Inativa">Inativa</SelectItem>
                      <SelectItem value="Suspensa">Suspensa</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Porte</Label>
                <Input
                  value={
                    account.accountType
                      ? (ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType)
                      : ''
                  }
                  disabled
                  readOnly
                  placeholder="Preenchido automaticamente"
                />
              </div>
              <div className="space-y-2">
                <Label>Natureza Jurídica</Label>
                <Input
                  value={account.natureText ?? ''}
                  disabled
                  readOnly
                  placeholder="Preenchido automaticamente"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Setor</Label>
              <div className="flex flex-wrap gap-3 p-3 border rounded-lg">
                {sectorOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={`setor-${option}`}
                      checked={selectedSetor.includes(option)}
                      onCheckedChange={(checked) => handleSetorChange(option, checked as boolean)}
                    />
                    <label htmlFor={`setor-${option}`} className="text-sm">
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={cnpjForm.formState.isSubmitting}>
                {cnpjForm.formState.isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={cpfForm.handleSubmit(handleCpfSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" {...cpfForm.register('name')} placeholder="Nome completo" />
              {cpfForm.formState.errors.name && (
                <p className="text-sm text-red-500">{cpfForm.formState.errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="rg">RG</Label>
              <Controller
                control={cpfForm.control}
                name="rg"
                render={({ field }) => (
                  <Input
                    id="rg"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(maskRg(e.target.value))}
                    placeholder="Número do RG"
                  />
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={cpfForm.formState.isSubmitting}>
                {cpfForm.formState.isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
